/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { isError } from "../../../utils/validations";
import { IKeyValue } from "../types";
import { ILoginRequest } from "../../../store/api/migration/types";
import { useStoreDispatch } from "../../../store/hooks";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Loader2 } from "lucide-react";

interface INewMigration {
  loginData: ILoginRequest;
  setLoginData: (value: React.SetStateAction<ILoginRequest>) => void;
  errMsg: IKeyValue;
  setErrMsg: (value: React.SetStateAction<IKeyValue>) => void;
  submitConnectHandler?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  loginStatus: any;
  onBlurHandler?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
}

const NewMigration: React.FC<INewMigration> = ({ loginData, setLoginData, errMsg, setErrMsg, submitConnectHandler, loginStatus, onBlurHandler }) => {
  const dispatch = useStoreDispatch();
  const [showPassword, setShowPassword] = React.useState(false);

  const handleOnChange = (event: any) => {
    event.preventDefault();
    const { name, value } = event.target;
    setLoginData({ ...loginData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose}>
        Cancel
      </Button>
      <Button onClick={submitConnectHandler} disabled={loginStatus.isLoading || isError(errMsg)}>
        {loginStatus.isLoading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
        Connect
      </Button>
    </>
  );

  return (
    <Sheet open={true} onClose={handleClose} title="Connect remote system" footer={footer} width="520px">
      <form autoComplete="off">
        <div className="space-y-5 pt-2">
          {/* URL */}
          <div className="space-y-2">
            <Label htmlFor="migration-url">URL <span style={{ color: "var(--destructive)" }}>*</span></Label>
            <Input
              id="migration-url"
              type="url"
              autoFocus={true}
              name="hostAddress"
              value={loginData?.hostAddress}
              onBlur={onBlurHandler}
              onChange={handleOnChange}
            />
            {errMsg?.hostAddress && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.hostAddress}</p>}
          </div>

          {/* User Name */}
          <div className="space-y-2">
            <Label htmlFor="migration-username">User Name <span style={{ color: "var(--destructive)" }}>*</span></Label>
            <Input
              id="migration-username"
              name="username"
              value={loginData?.username}
              onBlur={onBlurHandler}
              onChange={handleOnChange}
            />
            {errMsg?.username && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.username}</p>}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="migration-password">Password <span style={{ color: "var(--destructive)" }}>*</span></Label>
            <div style={{ position: "relative" }}>
              <Input
                id="migration-password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={loginData?.password}
                onBlur={onBlurHandler}
                onChange={handleOnChange}
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  padding: 4,
                  color: "var(--muted-foreground)",
                }}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {errMsg?.password && <p className="text-sm" style={{ color: "var(--destructive)" }}>{errMsg.password}</p>}
          </div>
        </div>
      </form>
    </Sheet>
  );
};

export default NewMigration;
